import { useRef, useState, type ChangeEvent, type FocusEvent, type KeyboardEvent, type RefObject } from 'react'

import { freshPrice, maskPrice } from '../price'

/**
 * Поле цены позиции — одно на полосу правки и на строку таблицы чека (RP-FB15: цену правят
 * там, где она видна). Показывает `saved` — цену, которую чек уже несёт; набранное живёт в
 * поле до Enter или ухода из поля, до этого чек не пересчитывается и курсор не прыгает.
 * Фокус выделяет ВСЁ поле: щелкнул — и сразу набирай новую цену, как в кассе 1С. Первая
 * набранная цифра заменяет рубли целиком («5» → «5,00»), копейки правятся вторым щелчком
 * в нужное место. Esc — отмена набора. Enter — применить и вернуть фокус в строку поиска
 * (`onDone`): сканер печатает туда.
 */
export function PriceField({
  saved,
  disabled,
  invalid,
  id,
  className,
  label,
  describedBy,
  onFocus,
  onCommit,
  onDone,
}: {
  saved: string
  disabled: boolean
  invalid: boolean
  id?: string
  className?: string
  /** Для поля без видимой подписи (строка таблицы). */
  label?: string
  describedBy?: string
  /** Поле получило фокус (строка таблицы становится выбранной). */
  onFocus?: () => void
  onCommit: (value: string) => void
  onDone?: () => void
}) {
  // Набираемая цена живёт в поле до Enter или ухода из поля.
  const [typing, setTyping] = useState<string | null>(null)
  const value = typing ?? saved
  // Первый ввод после входа в поле заменяет рубли целиком (как в кассе 1С).
  const freshEntry = useRef(false)
  // Щелчок, который дал полю фокус: отпускание кнопки не должно сбросить выделение в курсор.
  const focusedByClick = useRef(false)
  const commit = () => {
    if (typing !== null && typing !== saved) onCommit(typing)
    setTyping(null)
  }
  const onKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.currentTarget.blur()
      onDone?.()
    } else if (event.key === 'Escape') {
      // Esc — передумал: поле возвращает прежнюю цену.
      setTyping(null)
      freshEntry.current = true
    }
  }
  return (
    <input
      id={id}
      className={className}
      inputMode="decimal"
      value={value}
      placeholder={value === '' ? 'даст касса' : undefined}
      disabled={disabled}
      aria-label={label}
      aria-invalid={invalid}
      aria-describedby={describedBy}
      onFocus={(event) => {
        freshEntry.current = true
        focusedByClick.current = true
        selectAll(event)
        onFocus?.()
      }}
      onMouseUp={(event) => {
        // Без этого браузер, дорисовав щелчок, ставит курсор и снимает выделение, которое дал фокус.
        if (focusedByClick.current) event.preventDefault()
        focusedByClick.current = false
      }}
      onChange={(event) => {
        focusedByClick.current = false
        const text = typePrice(event, value, freshEntry)
        if (text !== null) setTyping(text)
      }}
      onKeyDown={onKey}
      onBlur={() => {
        focusedByClick.current = false
        commit()
      }}
    />
  )
}

/**
 * Ввод в поле цены через маску «рубли,копейки». Значение и курсор ставятся в
 * поле сразу: React, увидев в поле то же значение, курсор уже не двигает.
 * Возвращает новое значение поля; `null` — ввод не принят.
 */
function typePrice(
  event: ChangeEvent<HTMLInputElement>,
  previous: string,
  freshEntry: RefObject<boolean>,
): string | null {
  const input = event.target
  const caret = input.selectionStart ?? input.value.length
  const fresh = freshEntry.current ? freshPrice(input.value, previous) : null
  freshEntry.current = false
  const masked = fresh ?? maskPrice(input.value, caret, previous)
  if (masked === null) {
    input.value = previous
    input.setSelectionRange(Math.max(0, caret - 1), Math.max(0, caret - 1))
    return null
  }
  input.value = masked.text
  input.setSelectionRange(masked.caret, masked.caret)
  return masked.text
}

/**
 * Фокус выделяет всё поле. Сразу — для клавиатуры и тача; ещё раз после кадра — на случай,
 * когда браузер, закончив щелчок, поставил курсор поверх выделения.
 */
function selectAll(event: FocusEvent<HTMLInputElement>) {
  const input = event.currentTarget
  input.select()
  setTimeout(() => {
    if (document.activeElement === input && input.selectionStart === input.selectionEnd) input.select()
  }, 0)
}
